import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff, Globe, Pencil, Radio, Shield, Wifi } from 'lucide-react';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { WifiEditDialog } from './WifiEditDialog';
import { OmciWanDialog } from './OmciWanDialog';
import { Card, ErrorNotice, Pill, Spinner } from '../../components/ui';

interface OmciSsid {
  iface: string;
  index: number;
  name: string | null;
  hidden: boolean | null;
  isolation: boolean | null;
  maxUsers: number | null;
  currentUsers: number | null;
  auth: string | null;
  encryption: string | null;
  hasKey: boolean;
  /** The passphrase this platform last set over OMCI; the chassis never reads it back. */
  storedKey?: string | null;
}

interface OmciRadio {
  enabled: boolean | null;
  operational: boolean | null;
  workMode: string | null;
  bandwidthMhz: number | null;
  channel: number | null;
  txRate: string | null;
  txPowerPercent: number | null;
  regulatoryDomain: string | null;
}

interface OmciWan {
  /** management = the connection the platform manages the ONU over (its CVLAN is the TR-069 VLAN). */
  role?: 'management' | 'subscriber' | 'unknown';
  index: number | null;
  mode: string | null;
  vlanMode: string | null;
  cvlan: number | null;
  cvlanPriority: number | null;
  svlan: number | null;
  ip: string | null;
  mask: string | null;
  gateway: string | null;
  dns1: string | null;
  dns2: string | null;
  macAddress: string | null;
  hostId: number | null;
  pingResponse: boolean | null;
  tracerouteResponse: boolean | null;
}

interface HuaweiServicePort {
  index: number;
  vlanId: number;
  gemIndex: number | null;
  rxTable: number | null;
  txTable: number | null;
  state: string | null;
}

/**
 * One shape per vendor, because they know different things. A ZTE reports the
 * ONU's own wifi, SSIDs and WAN; a Huawei reports only the service it carries.
 * Flattening them would mean inventing Huawei fields that are always empty.
 */
interface OmciConfig {
  radio?: OmciRadio | null;
  ssids?: OmciSsid[];
  wan?: OmciWan | null;
  wans?: OmciWan[];
  servicePorts?: HuaweiServicePort[];
  /** Only a Huawei OLT's read carries this — what tells the two shapes apart. */
  portRoutes?: unknown;
  warnings: string[];
  interfaceName: string;
}

/** Flat, with the snapshot's own fields beside it — not wrapped in `data`. `unread`: no snapshot yet, nothing else set. */
type Response = OmciConfig & { fetchedAt?: number | null; cached?: boolean; unread?: boolean };

/**
 * An ONU's WAN and wifi, read off the OLT rather than from an ACS.
 *
 * The same subscriber facts the TR-069 cards show, for the ONUs that have never
 * informed TR-069 — which on this fleet is most of them. The OLT manages every
 * ONU over OMCI whether or not an ACS has heard from it, so the configuration
 * was always knowable; the platform had simply only ever asked GenieACS.
 *
 * No longer read-only: on ZTE the WAN and the SSIDs each carry an Edit that
 * writes back over `pon-onu-mng`, chassis-verified. Huawei stays read-only
 * because its OLTs are — wifi and the routed WAN are TR-069 questions there.
 */
export function OmciConfigCards({
  oltId,
  onuId,
  routerOwnsWifi = false,
}: {
  oltId: string;
  onuId: string;
  /**
   * The ONU is managed over TR-069, so its WiFi is the router's
   * (`wifiOwnedByRouter`): the SSIDs the OLT holds are shown, not edited —
   * the API refuses an OLT WiFi write for it (2026-10-05).
   */
  routerOwnsWifi?: boolean;
}) {
  const query = useQuery({
    queryKey: ['onu', onuId, 'omci-config'],
    queryFn: () =>
      api.get<Response>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/omci-config`,
      ),
  });

  const config = query.data;

  if (query.isLoading) {
    return (
      <Card title="Reading the ONU over OMCI">
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      </Card>
    );
  }
  if (query.error) {
    return (
      <Card title="The ONU's own configuration">
        <div className="p-5">
          <ErrorNotice error={query.error} />
        </div>
      </Card>
    );
  }
  if (!config) return null;
  /*
    No snapshot yet (`unread`): the route answers that alone, with no
    warnings or interface, and these cards used to crash the Configs tab on
    it (HS MC6 - Maggie3, 2026-10-14, right after a WAN delete invalidated
    the snapshot).
  */
  if (config.unread || !Array.isArray(config.warnings)) {
    return (
      <Card title="The ONU's own configuration">
        <p className="p-5 text-sm text-muted">
          Not read off the chassis yet — the page reads it on its next refresh, or press the refresh beside the card.
        </p>
      </Card>
    );
  }

  // A Huawei answers with service ports and nothing else; a ZTE with the ONU's
  // own radio, SSIDs and WAN. The wifi card is still drawn on a Huawei, saying
  // where wifi actually lives — an operator looking for it should find an
  // answer in the place they looked, not a card that quietly is not there.
  /*
    Told apart by `portRoutes`, which only a Huawei OLT's read carries — the
    rule the API's own adoption uses. It used to be `servicePorts`, which a
    ZTE read carries too, so a Huawei ONT behind gachie1 (HS MC6 - Maggie3,
    2026-10-14) was drawn as a Huawei OLT: "A Huawei OLT cannot report an
    ONT's wifi", and service rows with no VLAN in them.
  */
  if (config.portRoutes !== undefined && config.servicePorts) {
    return (
      <>
        <div className="grid items-start gap-5 xl:grid-cols-2">
          <ServicePortsCard ports={config.servicePorts} iface={config.interfaceName} />
          <WifiNeedsTr069 />
        </div>
        {/* The wifi note is inside the card above; anything else the read had
            to say still belongs here. */}
        {config.warnings
          .filter((w) => !/wifi or SSIDs/i.test(w))
          .map((w) => (
            <div
              key={w}
              className="rounded-lg border border-line bg-surface-2 px-4 py-2.5 text-sm text-muted"
            >
              {w}
            </div>
          ))}
      </>
    );
  }

  return (
    <>
      <div className="grid items-start gap-5 xl:grid-cols-2">
        <WanCard wan={config.wan ?? null} iface={config.interfaceName} oltId={oltId} onuId={onuId} />
        <RadioCard radio={config.radio ?? null} />
      </div>
      <SsidsCard
        ssids={config.ssids ?? []}
        radioOn={config.radio?.enabled ?? null}
        oltId={oltId}
        onuId={onuId}
        routerOwnsWifi={routerOwnsWifi}
      />
      {config.warnings.map((w) => (
        <div
          key={w}
          className="rounded-lg border border-warn/30 bg-warn-soft px-4 py-2.5 text-sm text-warn"
        >
          {w}
        </div>
      ))}
    </>
  );
}

/** The label every card carries, so nobody has to guess where a value came from. */
function ViaOmci() {
  return (
    <span
      className="text-[10px] uppercase tracking-wider text-faint"
      title="Read from the OLT over OMCI, not from a TR-069 server"
    >
      via the OLT
    </span>
  );
}

function Rows({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="grid">
      {rows.map(([label, value]) => (
        <div
          key={label}
          className="flex items-baseline justify-between gap-4 border-b border-line px-5 py-2"
        >
          <dt className="text-sm text-muted">{label}</dt>
          <dd className="truncate text-right font-mono text-xs text-ink">{value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

function WanCard({
  wan,
  iface,
  oltId,
  onuId,
}: {
  wan: OmciWan | null;
  iface: string;
  oltId: string;
  onuId: string;
}) {
  const { can } = useSession();
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // The chassis calls every wan-ip object "WAN"; on a TR-069-managed ONU the
  // one on the management VLAN is the platform's own connection to the ONU,
  // and a fleet migrated from SmartOLT carries only that one here — the
  // subscriber's PPPoE WAN lives on the ONU and shows under TR-069. Editing it
  // as if it were the subscriber's WAN is what the Mngt button is for.
  const management = wan?.role === 'management';
  const title = (
    <span className="flex items-center gap-2">
      <Globe className="size-4 text-muted" />
      {management ? 'Management connection' : 'WAN'}
      {wan?.mode && <Pill tone="info">{wan.mode}</Pill>}
      {management && <Pill tone="neutral">TR-069</Pill>}
      <ViaOmci />
      {can('onu.provision') && !management && (
        <button
          onClick={() => setEditing(true)}
          className="ml-1 inline-flex items-center gap-1 text-xs font-normal text-link hover:underline"
          title="Set DHCP or PPPoE on the ONU itself, through the OLT"
        >
          <Pencil className="size-3" /> Edit
        </button>
      )}
    </span>
  );

  if (editing) {
    return (
      <OmciWanDialog
        oltId={oltId}
        onuId={onuId}
        currentMode={wan?.mode ?? null}
        // The VLAN the ONU is actually on and the WAN interface number it is
        // on — so the dialog seeds the VLAN the card shows (not VLAN 1) and
        // writes back to the right interface (§8.4).
        wanCvlan={wan?.cvlan ?? null}
        wanIndex={wan?.index ?? null}
        onClose={() => setEditing(false)}
      />
    );
  }
  if (!wan) {
    return (
      <Card title={title}>
        {notice && <p className="border-b border-line px-5 py-2.5 text-sm text-success">{notice}</p>}
        <p className="p-5 text-sm text-muted">
          The chassis reported no WAN interface for {iface}. A bridged ONU with no IP host has none,
          which is normal rather than a fault.
        </p>
      </Card>
    );
  }
  return (
    <Card title={title}>
      {notice && <p className="border-b border-line px-5 py-2.5 text-sm text-success">{notice}</p>}
      {management && (
        <p className="border-b border-line px-5 py-2.5 text-xs text-muted">
          This is the connection the platform manages the ONU over (TR-069 on VLAN {wan.cvlan ?? '—'}),
          not the subscriber&apos;s WAN. The subscriber&apos;s WAN — PPPoE, DHCP or a bridge — is set on
          the ONU itself and appears under TR-069 once the ONU informs the ACS. Change the
          management connection with Mngt.
        </p>
      )}
      <Rows
        rows={[
          ['Addressing', wan.mode ?? '—'],
          ['IP address', wan.ip ?? '—'],
          ['Subnet mask', wan.mask ?? '—'],
          ['Gateway', wan.gateway ?? '—'],
          ['DNS', [wan.dns1, wan.dns2].filter(Boolean).join(', ') || '—'],
          [
            'VLAN',
            wan.cvlan
              ? `${wan.cvlan}${wan.cvlanPriority ? ` (p${wan.cvlanPriority})` : ''}${wan.svlan ? ` · S${wan.svlan}` : ''}`
              : 'untagged',
          ],
          ['MAC', wan.macAddress ?? '—'],
        ]}
      />
      {/* Whether the ONU answers from outside is a subscriber-visible choice,
          not a detail — it is the difference between a reachable device and a
          firewalled one, and the chassis sets it per WAN interface. */}
      <div className="flex flex-wrap items-center gap-2 px-5 py-3">
        <Shield className="size-3.5 text-muted" />
        <Pill state tone={wan.pingResponse ? 'warn' : 'success'}>
          {wan.pingResponse ? 'answers ping' : 'ignores ping'}
        </Pill>
        <Pill state tone={wan.tracerouteResponse ? 'warn' : 'success'}>
          {wan.tracerouteResponse ? 'answers traceroute' : 'ignores traceroute'}
        </Pill>
        <span className="text-[11px] text-faint">
          how reachable this ONU is from outside its own network
        </span>
      </div>
    </Card>
  );
}

function RadioCard({ radio }: { radio: OmciRadio | null }) {
  const title = (
    <span className="flex items-center gap-2">
      <Radio className="size-4 text-muted" />
      Wifi radio
      {radio && (
        <Pill state tone={radio.enabled ? 'success' : 'neutral'} dot>
          {radio.enabled ? 'on' : 'off'}
        </Pill>
      )}
      <ViaOmci />
    </span>
  );
  if (!radio) {
    return (
      <Card title={title}>
        <p className="p-5 text-sm text-muted">
          This ONU model has no wifi the chassis can report — an SFU or bridge, rather than a fault.
        </p>
      </Card>
    );
  }
  return (
    <Card title={title}>
      <Rows
        rows={[
          ['Mode', radio.workMode ?? '—'],
          // Channel 0 is the chassis saying "pick one", not channel zero.
          ['Channel', radio.channel === 0 ? 'auto' : (radio.channel ?? '—')],
          ['Bandwidth', radio.bandwidthMhz ? `${radio.bandwidthMhz} MHz` : '—'],
          ['Transmit rate', radio.txRate ?? '—'],
          ['Transmit power', radio.txPowerPercent === null ? '—' : `${radio.txPowerPercent}%`],
          ['Regulatory domain', radio.regulatoryDomain ?? '—'],
        ]}
      />
    </Card>
  );
}

function SsidsCard({
  ssids,
  radioOn,
  oltId,
  onuId,
  routerOwnsWifi,
}: {
  ssids: OmciSsid[];
  radioOn: boolean | null;
  oltId: string;
  onuId: string;
  routerOwnsWifi: boolean;
}) {
  const { can } = useSession();
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const named = ssids.filter((s) => s.name);
  // The passphrases this platform last set over OMCI, which the chassis masks on
  // read-back; shown behind a reveal so a shoulder-surfer does not read them off
  // an open page, the same treatment the TR-069 WiFi card gives its stored key.
  const anyStoredKey = ssids.some((s) => s.storedKey);
  const title = (
    <span className="flex items-center gap-2">
      <Wifi className="size-4 text-muted" />
      SSIDs
      <span className="text-xs font-normal text-faint">
        {named.length} named of {ssids.length}
      </span>
      <ViaOmci />
      {anyStoredKey && (
        <button
          onClick={() => setRevealed((v) => !v)}
          className="ml-1 inline-flex items-center gap-1 text-xs font-normal text-link hover:underline"
        >
          {revealed ? <EyeOff className="size-3" /> : <Eye className="size-3" />}
          {revealed ? 'Hide keys' : 'Show keys'}
        </button>
      )}
      {can('onu.provision') && !routerOwnsWifi && (
        <button
          onClick={() => setEditing(true)}
          className="ml-1 inline-flex items-center gap-1 text-xs font-normal text-link hover:underline"
          title="Change names, passphrases and radios through the OLT"
        >
          <Pencil className="size-3" /> Edit
        </button>
      )}
    </span>
  );
  if (ssids.length === 0) return null;

  if (editing) {
    return (
      <WifiEditDialog
        oltId={oltId}
        onuId={onuId}
        radioOn={radioOn}
        ssids={ssids.map((s) => ({ port: s.iface, name: s.name, hidden: s.hidden }))}
        onClose={() => setEditing(false)}
      />
    );
  }

  return (
    <Card title={title}>
      {routerOwnsWifi && (
        <p className="border-b border-line px-5 py-2.5 text-sm text-muted">
          This ONU is managed over TR-069, so its WiFi is the router&rsquo;s: change it on the WiFi card.
          These are the lines the OLT holds, not what the router broadcasts. On a ZTE router the OLT
          re-applies them at every registration, and the Drift page offers to take them off.
        </p>
      )}
      {notice && <p className="border-b border-line px-5 py-2.5 text-sm text-success">{notice}</p>}
      <div className="divide-y divide-line">
        {ssids.map((ssid) => (
          <div key={ssid.iface} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3">
            <span className="w-20 shrink-0 font-mono text-xs text-faint">{ssid.iface}</span>
            <span className="min-w-0 flex-1 truncate text-sm text-ink">
              {ssid.name || <span className="text-faint">not named</span>}
            </span>
            {ssid.hidden && <Pill tone="neutral">hidden</Pill>}
            {ssid.isolation && <Pill tone="neutral">isolated</Pill>}
            {ssid.auth && <span className="text-[11px] text-muted">{ssid.auth}</span>}
            {/* The chassis masks the key on read-back; the platform shows the one
                it last set over OMCI, from its own store, revealed on request. */}
            <Pill state tone={ssid.hasKey || ssid.storedKey ? 'success' : 'danger'}>
              {ssid.hasKey || ssid.storedKey ? 'secured' : 'open'}
            </Pill>
            {ssid.storedKey && (
              <span className="font-mono text-[11px] text-muted">
                {revealed ? ssid.storedKey : '••••••••'}
              </span>
            )}
            <span className="text-[11px] tabular-nums text-faint">
              {ssid.currentUsers ?? 0}/{ssid.maxUsers ?? '—'} clients
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * The VLANs a Huawei ONT carries, which GEM each rides and how it is shaped.
 *
 * This is what a Huawei OLT knows about an ONT's configuration — there is no
 * `display ont wifi` on this firmware, and `port attribute` takes only `catv`.
 * Huawei manages an ONT's wifi over TR-069 instead, which is why every ONT in
 * this fleet carries an `ont tr069-server-config` line.
 */
function ServicePortsCard({
  ports,
  iface,
}: {
  ports: HuaweiServicePort[];
  iface: string;
}) {
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Globe className="size-4 text-muted" />
          Service
          <span className="text-xs font-normal text-faint">
            {ports.length} VLAN{ports.length === 1 ? '' : 's'} on {iface}
          </span>
          <ViaOmci />
        </span>
      }
    >
      {ports.length === 0 ? (
        <p className="p-5 text-sm text-muted">
          This ONT carries no service yet — no VLAN has been attached to it.
        </p>
      ) : (
        <div className="divide-y divide-line">
          {ports.map((p) => (
            <div key={p.index} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3">
              <Pill tone="info">VLAN {p.vlanId}</Pill>
              <span className="text-xs text-muted">GEM {p.gemIndex ?? '—'}</span>
              <span className="font-mono text-[11px] text-faint">port {p.index}</span>
              {/* A dash in the chassis output means unshaped, which is a rate
                  of no limit rather than a rate of zero. */}
              <span className="text-[11px] text-faint">
                {p.rxTable === null && p.txTable === null
                  ? 'unshaped'
                  : `traffic table ${p.rxTable ?? '—'} / ${p.txTable ?? '—'}`}
              </span>
              <Pill tone={p.state === 'up' ? 'success' : 'danger'} dot>
                {p.state ?? 'unknown'}
              </Pill>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * The wifi card on a Huawei, which cannot report wifi.
 *
 * Drawn rather than omitted. An operator opening this page to check a
 * subscriber's SSID needs an answer where they looked; a card that silently is
 * not there reads as a page still loading, or as an ONU with no wifi at all.
 * Neither is true — the ONT has wifi, and this chassis is simply not the thing
 * that knows about it.
 */
function WifiNeedsTr069() {
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Wifi className="size-4 text-muted" />
          Wifi
          <Pill tone="warn">needs TR-069</Pill>
        </span>
      }
    >
      <div className="space-y-3 p-5">
        <p className="text-sm text-muted">
          A Huawei OLT cannot report an ONT&rsquo;s wifi. There is no command for it: the radio and
          its SSIDs are managed over TR-069, not through the chassis.
        </p>
        <p className="text-sm text-muted">
          Put this ONT on TR-069 and its SSIDs, security and connected clients appear here — the
          same view a ZTE gives over OMCI.
        </p>
        <p className="text-[11px] text-faint">
          Use <span className="text-ink">OMCI / TR-069</span> above to switch it over.
        </p>
      </div>
    </Card>
  );
}
